import { IonToast } from '@ionic/react';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { Icon } from '../components/chancey';
import { SupportPageLayout } from '../components/layout/SupportPageLayout';
import { TicketDetailSheet } from '../components/tickets/TicketDetailSheet';
import { TicketSnapshotCard } from '../components/tickets/TicketSnapshotCard';
import { useGameApi } from '../api/games';
import { useActiveGame } from '../hooks/useActiveGame';
import { useScannedTicketsForGame } from '../hooks/useScannedTickets';
import { useTicketDetailSheetController } from '../hooks/useTicketDetailSheetController';
import { useChanceyTheme } from '../theme/ThemeContext';

const PAGE_SIZE = 10;

const TicketSnapshotsPage: React.FC = () => {
  const { c, typ } = useChanceyTheme();
  const history = useHistory();
  const { game, gameId } = useActiveGame();
  const { getGameDraw } = useGameApi();
  const { tickets, displayTickets, addTicket, updateTicket, removeTicket } =
    useScannedTicketsForGame(gameId);
  const [page, setPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(displayTickets.length / PAGE_SIZE));
  const pageTickets = React.useMemo(
    () => displayTickets.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [displayTickets, page]
  );
  const ticketSheet = useTicketDetailSheetController({
    tickets,
    gameId,
    getGameDraw,
    addTicket,
    updateTicket,
    removeTicket,
  });

  React.useEffect(() => {
    setPage(1);
  }, [gameId]);

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <SupportPageLayout title="Saved Tickets" eyebrow={game.name} fallbackPath="/home">
      {displayTickets.length === 0 ? (
        <button
          type="button"
          onClick={() => history.push('/scan')}
          className="w-full rounded-[20px] border p-5 text-left"
          style={{ background: c.surface, borderColor: c.line }}
        >
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl"
            style={{ background: c.ink, color: c.bg }}
          >
            {Icon.cam(22, c.bg)}
          </div>
          <div className="mt-3 text-[18px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
            Scan your first {game.shortName} ticket
          </div>
          <div className="mt-1 text-[13px]" style={{ color: c.inkSoft, fontFamily: typ.body }}>
            Saved ticket snapshots will appear here.
          </div>
        </button>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {pageTickets.map((ticket) => (
              <TicketSnapshotCard
                key={ticket.id}
                ticket={ticket}
                c={c}
                typ={typ}
                variant="list"
                onOpen={ticketSheet.openTicket}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-45"
              style={{ background: c.surface, color: c.ink, border: `0.5px solid ${c.line}`, fontFamily: typ.body }}
            >
              Previous
            </button>
            <div className="text-xs" style={{ color: c.inkMuted, fontFamily: typ.body }}>
              Page {page} of {totalPages}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-45"
              style={{ background: c.ink, color: c.bg, fontFamily: typ.body }}
            >
              Next
            </button>
          </div>

        </>
      )}

      <TicketDetailSheet
        ticket={ticketSheet.selectedTicket}
        onClose={ticketSheet.closeSheet}
        onDelete={ticketSheet.handleDelete}
        onRefreshResult={ticketSheet.handleRefreshTicket}
        refreshing={ticketSheet.refreshing}
        refreshError={ticketSheet.refreshError}
        c={c}
        typ={typ}
        triggerRef={ticketSheet.triggerRef}
      />
      <IonToast
        isOpen={ticketSheet.toastOpen}
        message="We removed that ticket."
        duration={5000}
        onDidDismiss={() => {
          ticketSheet.setToastOpen(false);
          ticketSheet.clearUndoTicket();
        }}
        buttons={[
          {
            text: 'Undo',
            role: 'cancel',
            handler: () => {
              void ticketSheet.handleUndo();
            },
          },
        ]}
      />
    </SupportPageLayout>
  );
};

export default TicketSnapshotsPage;
